import { Component } from 'react'
import * as cn from 'classnames'
import * as formStyles from 'styles/molecules/forms/Form.css'
import styles from 'components/features/pfm/CarEditForm/CarEditForm.styl'
import { Field, reduxForm, getFormValues } from 'redux-form'
import { connect } from 'react-redux'
import { FORM_CARS_EDIT } from 'constants/forms'
import { FIELD_PURCHASED_PROPS, FIELD_CAR_ESTIMATED_PROPS, FIELD_CURRENCY_WRAPPED_PROPS } from 'constants/fields'
import { RawCarFormValues, BaseCar, Car } from 'interfaces/property'
import { ReduxFormProps } from 'interfaces/component'
import { State } from 'interfaces/state'
import { carSchema } from 'schemas/propertySchema'
import { asyncValidator } from 'helpers/schemaHelpers'
import { getSaveButtonFormProps, shouldAsyncValidate } from 'helpers/formHelpers'
import { getCarModelsActionCreator } from 'actions/propertyActions'
import Deferred from 'components/utils/Deferred'
import { CarMakeField, CarYearField, CarModelField } from 'components/atoms/inputs/CarFields'
import SelectWrapped from 'components/atoms/inputs/OldSelectWrapped'
import Button from 'components/features/pfm/Button/Button'
import TextFieldWrapped from 'components/atoms/inputs/TextFieldWrapped'
import { CarUnlinkHandler } from 'components/features/pfm/CarUnlinkHandler/CarUnlinkHandler'
import { CarDeleteHandler } from 'components/features/pfm/CarDeleteHandler/CarDeleteHandler'
import { carByIdSelector } from 'selectors/propertySelectors'

export type CarEditFormStateProps = {
  makeValue: number | null
  modelValue: number | null
  yearValue: number | null
  car: Car | null
}

export type CarEditFormDispatchProps = {
  getCarModels: typeof getCarModelsActionCreator
}

export type CarEditFormOwnProps =
  & ReduxFormProps
  & {
    carId: BaseCar['id'] | null
    onCancel: () => void
    onUnlink: () => void
    onDelete: () => void
  }

type CarEditFormProps =
  & CarEditFormStateProps
  & CarEditFormDispatchProps
  & CarEditFormOwnProps

@reduxForm({
  form: FORM_CARS_EDIT,
  asyncValidate: asyncValidator(carSchema),
  shouldAsyncValidate,
})
class CarEditFormComponent extends Component<CarEditFormProps, {}> {

  render () {
    const {
      handleSubmit, onCancel,
      makeValue, modelValue,
      onUnlink, onDelete, carId,
    } = this.props

    const editDeleteButtons = carId && carId !== -1 ? (
      <div className={ styles.buttonsRow }>
        <div className={ styles.button }>
          <CarUnlinkHandler carId={ carId } onConfirm={ onUnlink }>
            <Button size='small' theme='lightBlue' qaMarker='unlink-button' fullWidth>
              Unlink
            </Button>
          </CarUnlinkHandler>
        </div>
        <div className={ styles.button }>
          <CarDeleteHandler carId={ carId } onConfirm={ onDelete }>
            <Button size='small' theme='lightBlue' qaMarker='delete-button' fullWidth>
              Delete
            </Button>
          </CarDeleteHandler>
        </div>
      </div>
    ) : null

    const buttons = (
      <>
        <Button text='Save' submit { ...getSaveButtonFormProps } fullWidth />
        <div className={ formStyles.buttonRowWrapper }>
          { editDeleteButtons }
        </div>
        <Button
          text='Cancel'
          style='textOnly'
          onClick={ onCancel }
          fullWidth
          qaMarker='cancel-button'
        />
      </>
    )

    return (
      <form onSubmit={ handleSubmit } className={ formStyles.onSettingsPage }>
        <ul className={ formStyles.form }>
          <li className={ formStyles.item }>
            <CarMakeField
              label='Make'
              name='make'
              qaMarker='make'
            />
          </li>
          <li className={ formStyles.item }>
            <CarModelField
              label='Model'
              name='model'
              make={ makeValue }
              qaMarker='model'
            />
          </li>
          <li className={ formStyles.item }>
            <CarYearField
              label='Year'
              name='year'
              make={ makeValue }
              model={ modelValue }
              qaMarker='year'
            />
          </li>
          <li className={ cn(formStyles.item, styles.dropdownHeight) }>
            <Field
              name='purchased_at'
              props={ FIELD_PURCHASED_PROPS }
              component={ SelectWrapped }
            />
          </li>
          <li className={ formStyles.item }>
            <Field
              name='estimated_value'
              props={ FIELD_CAR_ESTIMATED_PROPS }
              component={ TextFieldWrapped }
              { ...FIELD_CURRENCY_WRAPPED_PROPS }
            />
          </li>
        </ul>
        <div className={ cn(formStyles.form, styles.submitButtons) }>
          { buttons }
        </div>
      </form>
    )
  }

  componentDidMount () {
    const { getCarModels, car } = this.props

    if (!car) {
      return
    }

    getCarModels(car.make.id)
  }
}

const mapStateToProps = (state: State, props: CarEditFormOwnProps): CarEditFormStateProps => {
  const values = getFormValues(FORM_CARS_EDIT)(state) as RawCarFormValues || null
  const makeValue = values ? values.make : null
  const modelValue = values ? values.model : null
  const yearValue = values ? values.year : null

  return {
    makeValue,
    modelValue,
    yearValue,
    car: carByIdSelector(state, props),
  }
}

const mapDispatchToProps: CarEditFormDispatchProps = {
  getCarModels: getCarModelsActionCreator,
}

const CarEditForm = Deferred(connect<
  CarEditFormStateProps,
  {},
  CarEditFormOwnProps
>(mapStateToProps, mapDispatchToProps)(CarEditFormComponent))

// Deferred should fix bug when previous form is not destroyed but new one mounts
export default CarEditForm
